/**
 * PATH: packages/ui/src/mobile/edgeSnapCorner.ts
 * PURPOSE: Snap a floating control to the nearest viewport corner or edge.
 * WHY: Phone docks and FABs need a persisted, thumb-reachable home that still
 *      defaults to today's bottom-left / bottom-right placement.
 */

export type SnapCorner = 'tl' | 'tr' | 'bl' | 'br'
export type SnapEdge = 'top' | 'bottom'

export function snapCornerFromPoint(
  x: number,
  y: number,
  vw: number,
  vh: number,
): SnapCorner {
  const left = x < vw / 2
  const top = y < vh / 2
  if (top) return left ? 'tl' : 'tr'
  return left ? 'bl' : 'br'
}

export function snapEdgeFromPoint(y: number, vh: number): SnapEdge {
  return y < vh / 2 ? 'top' : 'bottom'
}

export function loadSnapValue<T extends string>(key: string, fallback: T, allowed: readonly T[]): T {
  if (typeof localStorage === 'undefined') return fallback
  try {
    const raw = localStorage.getItem(key)
    if (raw && (allowed as readonly string[]).includes(raw)) return raw as T
  } catch {
    /* private mode */
  }
  return fallback
}

export function saveSnapValue(key: string, value: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(key, value)
  } catch {
    /* private mode */
  }
}

const CORNERS: readonly SnapCorner[] = ['tl', 'tr', 'bl', 'br']
const EDGES: readonly SnapEdge[] = ['top', 'bottom']

export function loadSnapCorner(key: string, fallback: SnapCorner): SnapCorner {
  return loadSnapValue(key, fallback, CORNERS)
}

export function loadSnapEdge(key: string, fallback: SnapEdge): SnapEdge {
  return loadSnapValue(key, fallback, EDGES)
}
